import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Drawer } from "../index";
import CustomButton from '../../common/CustomButton';
import SingleSelect from '../../common/Search/SingleSelect';
import MultiSelect from '../../common/Search/MultiSelect';
import StateMappingComponent from "../StateMappingComponent";
import useJiraService from "../../../services/jira";
import useOrganizationService from '../../../services/organization';
import SprintsList from '../SprintsList';
import { useKickoffAndPoll } from '../../../hooks/useKickoffAndPoll';
import toast from "react-hot-toast";
import { useDispatch, useSelector } from 'react-redux';
import { setJiraStatesSyncRunning } from '../../../redux/slices/organization';
import UnsavedChangesIndicator from '../../common/UnsavedChangesIndicator';
import ManageProjectsModal from './ManageProjectsModal';
import { HiInformationCircle } from "react-icons/hi";
import { SiJira } from "react-icons/si";
import { useJwt } from '../../../helpers/common';
import PriorityMappingSelector from "../../../features/PriorityLevels/PriorityLevelsList/PriorityMappingSelector";

interface ImportProjectModalProps {
  showModal: boolean;
  closeModal: () => void
  onImportSuccess?: () => void;
  onBack?: () => void;
  initialTab?: 'import' | 'states' | 'sprints' | 'priorities_mapping';
}

interface ProjectOption {
  value: string;
  label: string;
}

interface EpicOption {
  value: string;
  label: string;
}

interface EpicOption {
  value: string;
  label: string;
}

interface BoardOption {
  label: string;
  value: string;
}

// Skeleton Components
const SkeletonLoader = () => (
  <div className="space-y-6">
    {/* Project Selection Skeleton */}
    <div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
    </div>

    {/* Epic Selection Skeleton */}
    <div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
    </div>

    {/* Board Selection Skeleton */}
    <div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
    </div>

    {/* Configure Button Skeleton */}
    <div className="mt-6">
      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
    </div>
  </div>
);

// State Mapping Modal Component
const StateMappingModal = ({ isOpen, onClose, localMappings, setLocalMappings, localMappingsOrder, isJsonView, setIsJsonView, jsonInput, setJsonInput }: {
  isOpen: boolean;
  onClose: () => void;
  localMappings: Record<string, any>;
  setLocalMappings: (mappings: Record<string, any>) => void;
  localMappingsOrder?: Record<string, any>;
  isJsonView: boolean;
  setIsJsonView: (isJsonView: boolean) => void;
  jsonInput: string;
  setJsonInput: (jsonInput: string) => void;
}) => {
  if (!isOpen) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
    >
      <div className="h-full bg-tertiary-light dark:bg-tertiary-dark flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-500 rounded-b-2xl dark:border-gray-700 px-6 py-8 bg-secondary-light dark:bg-secondary-dark shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-light-text-primary dark:text-white">
              Configure State Mappings
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200"
            >
              <svg className="w-5 h-5 text-light-text-secondary dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* State Mapping Content */}
        <div className="flex-1 overflow-auto
        ">
          <StateMappingComponent
            localMappings={localMappings}
            setLocalMappings={setLocalMappings}
            localMappingsOrder={localMappingsOrder}
            isJsonView={isJsonView}
            setIsJsonView={setIsJsonView}
            jsonInput={jsonInput}
            setJsonInput={setJsonInput}
            isLoading={false}
          />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 left-0 right-0 p-4 bg-secondary-light dark:bg-secondary-dark border-t border-gray-500 rounded-t-md dark:border-gray-700 flex justify-end items-center shadow-lg z-10">
          <CustomButton
            onClick={onClose}
            variant="primary"
            className="bg-primary-light hover:bg-primary-hover"
          >
            Done
          </CustomButton>
        </div>
      </div>
    </Drawer>
  );
};

const ImportFromJiraModal = ({ closeModal, showModal, onImportSuccess, onBack, initialTab }: ImportProjectModalProps) => {
  const LAST_JIRA_PROJECT_KEY = 'cadence:lastJiraProjectId';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwtData: any = useJwt();
  const org_id = jwtData.org_id;
  const {
    fetchJiraProjects,
    fetchJiraEpics,
    importJiraEpic,
    updateJiraStages,
    fetchJiraSettings,
    manageJiraProjects,
    refreshJiraStatusStages,
  } = useJiraService();

  const {
    fetchSprints,
    syncIntegrationProject,
    getJiraSettings,
    refreshJiraPriorities,
    updateJiraPriorities,
  } = useOrganizationService();
  const { jiraSettings, jiraSettingsLoading, orgDetails } = useSelector((state: any) => state.organization);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isNotAuthenticated = jiraSettings?.is_authenticated === false;

  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoadingEpics, setIsLoadingEpics] = useState(false);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [epics, setEpics] = useState<EpicOption[]>([]);
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedEpicIds, setSelectedEpicIds] = useState<string[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const location = useLocation();
  const isDevMode = location.pathname.includes('/v');

  // State mapping states for Import tab
  const [localMappings, setLocalMappings] = useState<Record<string, any>>({});
  const [localMappingsOrder, setLocalMappingsOrder] = useState<Record<string, any>>({});
  const [jsonInput, setJsonInput] = useState('');
  const [isJsonView, setIsJsonView] = useState(false);

  // Separate state for Project States tab
  const [statesMappings, setStatesMappings] = useState<Record<string, any>>({});
  const [statesMappingsOrder, setStatesMappingsOrder] = useState<Record<string, any>>({});
  const [statesJsonInput, setStatesJsonInput] = useState('');
  const [isStatesJsonView, setIsStatesJsonView] = useState(false);
  const [priorityHasChanges, setPriorityHasChanges] = useState(false);
  const [isSavingPriorities, setIsSavingPriorities] = useState(false);

  const priorityRef = useRef<any>(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [savedStatesMappings, setSavedStatesMappings] = useState<Record<string, any>>({});
  const [savedStatesMappingsOrder, setSavedStatesMappingsOrder] = useState<Record<string, any>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);


  const isSyncing = useSelector((state: any) => state.organization.jiraStatesSyncRunning);
  const { start: startSync } = useKickoffAndPoll({
    kickoff: () => refreshJiraStatusStages(false, false),
    check: () => refreshJiraStatusStages(true, true),
    onKickoff: () => { },
    onComplete: async () => {
      dispatch(setJiraStatesSyncRunning(false));
      await loadSettings();
    },
    options: {
      intervalMs: 1000,
      maxWaitMs: 5 * 60 * 1000,
      persistAcrossUnmount: true,
      onStatusChange: (running) => dispatch(setJiraStatesSyncRunning(running))
    }
  });

  // Add state for display_message from API
  const [displayMessage, setDisplayMessage] = useState<string | null>(null);

  // Add new state to control visibility of the state mapping UI
  const [showStateMappings, setShowStateMappings] = useState(false);

  // Add state for the tab selection (Import or Project States)
  const [activeTab, setActiveTab] = useState<'import' | 'states' | 'sprints' | 'priorities_mapping'>('import');

  // State for separate modals
  const [showStateMappingModal, setShowStateMappingModal] = useState(false);
  const [showManageProjectsModal, setShowManageProjectsModal] = useState(false);

  // State for processed projects (project visibility)
  const [processedProjects, setProcessedProjects] = useState<Record<string, { hidden: boolean }>>({});

  const [showEpicLimitWarning, setShowEpicLimitWarning] = useState(false);

  // Helper function to synchronize JSON with mappings object
  const syncJsonWithMappings = (mappings: Record<string, any>, setJson: (json: string) => void) => {
    setJson(JSON.stringify(mappings, null, 2));
  };

  // Check authentication and fetch Jira projects
  useEffect(() => {
    if (showModal) {
      setIsCheckingAuth(true);
      getJiraSettings()
        .then((res: any) => {
          if (res?.data?.is_authenticated !== false) {
            getProjects();
            loadSettings(res?.data);
            // Set the initial tab if provided
            if (initialTab) {
              setActiveTab(initialTab);
            }
          }
        })
        .catch(() => {})
        .finally(() => setIsCheckingAuth(false));
    } else {
      setIsCheckingAuth(true);
    }
  }, [showModal, initialTab]);

  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem(LAST_JIRA_PROJECT_KEY, selectedProjectId);
    }
  }, [selectedProjectId]);

  // Fetch epics when a project is selected
  useEffect(() => {
    if (selectedProjectId) {
      // Reset state mappings, epic selection, and hide the mapping UI when project changes
      setShowStateMappings(false);
      setLocalMappings({});
      setLocalMappingsOrder({});
      setJsonInput('');
      setSelectedEpicIds([]);
      setSelectedBoard(''); // Reset epic selection
      setDisplayMessage(null); // Reset display message
      setShowEpicLimitWarning(false);

      // Set loading state immediately when project changes
      setIsLoadingEpics(true);
      setEpics([]); // Clear the epics array immediately to prevent showing old epics
      setBoards([]); // Clear the boards array immediately

      // Find selected project to get the name for API call
      const selectedProject = projects.find(p => p.value === selectedProjectId);
      if (selectedProject) {
        getEpics(selectedProject.label, selectedProjectId);
      }
    } else {
      setEpics([]);
      setBoards([]);
      setSelectedEpicIds([]);
      setSelectedBoard('');
      setDisplayMessage(null);
      setShowEpicLimitWarning(false);
      setIsLoadingEpics(false);
    }
  }, [selectedProjectId, projects]);

  // Update JSON when localMappings change (when in UI view) in Import tab
  useEffect(() => {
    if (!isJsonView) {
      syncJsonWithMappings(localMappings, setJsonInput);
    }
  }, [localMappings, isJsonView]);

  // Update JSON when statesMappings change (when in UI view) in States tab
  useEffect(() => {
    if (!isStatesJsonView) {
      syncJsonWithMappings(statesMappings, setStatesJsonInput);
    }
  }, [statesMappings, isStatesJsonView]);

  const getProjects = async () => {
    try {
      setIsLoading(true);
      const projectsList = await fetchJiraProjects();

      // Sort projects alphabetically by label
      const sortedProjects = [...projectsList].sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      setProjects(sortedProjects);

      const lastId = localStorage.getItem(LAST_JIRA_PROJECT_KEY) || '';
      if (!selectedProjectId && lastId && sortedProjects.some(p => p.value === lastId)) {
        setSelectedProjectId(lastId);
      }
    } catch (error) {
      // Error is already handled in the service
    } finally {
      setIsLoading(false);
    }
  };

  const getEpics = async (projectName: string, projectId: string) => {
    try {
      // Note: We now set isLoadingEpics in the useEffect when project changes
      const { epics: epicsList, boards: boardsList, statusStages, statusStagesOrder, displayMessage } = await fetchJiraEpics(projectName, projectId);

      // Set the display message from API, preserving empty strings
      // This allows the UI to distinguish between null and empty string cases
      // We store the message but only show it once an epic is selected
      setDisplayMessage(displayMessage !== undefined ? displayMessage : '');

      // Sort epics: non-imported first, imported last, both sorted by id in descending order
      const sortedEpics = [...epicsList].sort((a: any, b: any) => {
        const aImported = Boolean(a?.isImported || a?.isDisabled);
        const bImported = Boolean(b?.isImported || b?.isDisabled);
        if (aImported !== bImported) {
          return aImported ? 1 : -1;
        }
        // Sort by id in descending order (highest id first)
        const aId = a?.id ?? 0;
        const bId = b?.id ?? 0;
        return bId - aId;
      });

      const sortedBoards = [...boardsList].sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      setBoards(sortedBoards)
      setEpics(sortedEpics);

      // If we have status stages, update the localMappings and JSON for Import tab only
      if (statusStages) {
        setLocalMappings(statusStages);
        setLocalMappingsOrder(statusStagesOrder || {});
        syncJsonWithMappings(statusStages, setJsonInput);
      } else {
        // If no status stages were returned, create a default empty structure
        const defaultMapping = {};
        setLocalMappings(defaultMapping);
        setLocalMappingsOrder({});
        syncJsonWithMappings(defaultMapping, setJsonInput);
      }
    } catch (error) {
      // Error is already handled in the service
      // In case of error, set epics to empty array to avoid showing old data
      setEpics([]);
      setBoards([]);
    } finally {
      setIsLoadingEpics(false);
    }
  };

  const handleImport = async () => {
    if (!selectedProjectId || selectedEpicIds.length === 0 || !selectedBoard) {
      toast.error('Please select a project, at least one epic, and a board');
      return;
    }

    try {
      setIsImporting(true);
      // Find selected project to get the label for API call
      const selectedProject = projects.find(p => p.value === selectedProjectId);

      if (selectedProject) {
        // Parse the current JSON input to get the latest status stages
        let statusStages;
        if (isJsonView) {
          try {
            statusStages = JSON.parse(jsonInput);
          } catch (error) {
            toast.error('Invalid JSON format. Please fix before importing.');
            setIsImporting(false);
            return;
          }
        } else {
          statusStages = localMappings;
        }

        await importJiraEpic(selectedEpicIds, selectedProject.label, selectedProjectId, selectedBoard, statusStages);

        if (onImportSuccess) {
          onImportSuccess();
        }
        handleClose();
      }
    } catch (error) {
      // Error is already handled in the service
    } finally {
      setIsImporting(false);
    }
  };

  const handleSaveStateMappings = async () => {
    try {
      setIsLoadingStates(true);

      let statusStages: Record<string, any>;

      // Get the current state mappings based on view mode
      if (isStatesJsonView) {
        try {
          statusStages = JSON.parse(statesJsonInput);

          // Validate that statusStages is an object
          if (typeof statusStages !== 'object' || statusStages === null) {
            throw new Error('Status stages must be a valid object');
          }
        } catch (error) {
          console.error('Error parsing JSON:', error);
          toast.error('Invalid JSON format. Please fix before saving.');
          setIsLoadingStates(false);
          return;
        }
      } else {
        statusStages = statesMappings;
      }

      await updateJiraStages(statusStages);

      // Refresh the Azure settings after saving
      await loadSettings();

      toast.success('State mappings saved successfully!');
      setSavedStatesMappings(JSON.parse(JSON.stringify(statusStages || {})));
      setSavedStatesMappingsOrder(JSON.parse(JSON.stringify(statesMappingsOrder || {})));
    } catch (error) {
      console.error('Error saving state mappings:', error);
      toast.error('Error saving state mappings');
    } finally {
      setIsLoadingStates(false);
    }
  };

  const handleClose = () => {
    setSelectedProjectId('');
    setSelectedEpicIds([]);
    setSelectedBoard('');
    setShowStateMappings(false);
    setDisplayMessage(null);
    setActiveTab('import');
    setShowStateMappingModal(false);
    setLocalMappingsOrder({});
    setStatesMappingsOrder({});
    setShowEpicLimitWarning(false);
    closeModal()
  };

  // Add a new function to load Jira settings using the service
  const loadSettings = async (existingData?: any) => {
    try {
      setIsLoadingStates(true);
      const result = await fetchJiraSettings(existingData)
      const { parsedStages, parsedStagesOrder, processedProjects: fetchedProjects } = result;

      // Set the state mappings and JSON input



      setStatesMappings(parsedStages);
      setStatesMappingsOrder(parsedStagesOrder || {});
      syncJsonWithMappings(parsedStages, setStatesJsonInput);
      setSavedStatesMappings(JSON.parse(JSON.stringify(parsedStages || {})));
      setSavedStatesMappingsOrder(JSON.parse(JSON.stringify(parsedStagesOrder || {})));

      // Set processed projects for project visibility management
      if (fetchedProjects) {
        setProcessedProjects(fetchedProjects);
      }

     
    } catch (error) {
      console.error('Error fetching Jira settings:', error);
      toast.error('Error fetching Jira settings');
      setStatesMappings({});
      setStatesMappingsOrder({});
      setStatesJsonInput('{}');
      setSavedStatesMappings({});
      setSavedStatesMappingsOrder({});
    } finally {
      setIsLoadingStates(false);
    }
  };



  // Update the function to handle clicking on Configure State Mappings
  const handleShowStateMappings = () => {
    // Clear the display message when configuring state mappings
    setDisplayMessage(null);
    setShowStateMappingModal(true);
  };

  // Update the function to handle refreshing status stages
  const handleRefreshStatusStages = async () => {
    try {
      await startSync();
    } catch (error) {
      console.error("Sync failed:", error);
      dispatch(setJiraStatesSyncRunning(false));
    }
  };

  // Determine when to show the Configure State Mappings button
  const shouldShowConfigureStateButton = useMemo(() => {
    // Show the button when:
    // 1. Project and epic are selected
    // 2. State mappings are not already shown
    // 3. displayMessage is either null or has actual content (not an empty string)
    return selectedProjectId &&
      selectedEpicIds.length > 0 &&
      selectedBoard &&
      !showStateMappings &&
      displayMessage !== '';
  }, [selectedProjectId, selectedEpicIds, selectedBoard, showStateMappings, displayMessage]);

  return (
    <>
      <Drawer isOpen={showModal} onClose={handleClose} position="right">
        <div
          tabIndex={-1}
          className="h-full bg-tertiary-light dark:bg-tertiary-dark flex flex-col"
        >
          {/* Header with global color system */}
          <div className="sticky top-0 z-20 shrink-0 border-b border-gray-500 rounded-b-2xl dark:border-gray-700 px-6 py-8 bg-secondary-light dark:bg-secondary-dark shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200 group"
                    title="Back to import options"
                  >
                    <svg
                      className="w-5 h-5 text-light-text-secondary dark:text-gray-400 group-hover:text-light-text-primary dark:group-hover:text-white transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}
                <h2 className="text-xl font-semibold text-light-text-primary dark:text-white">
                  Jira Integration
                </h2>
              </div>
              {/* Manage Projects Button */}
              {!isNotAuthenticated && (
                <button
                  onClick={() => setShowManageProjectsModal(true)}
                  className="group flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium bg-primary-light hover:bg-primary-hover dark:bg-primary-dark dark:hover:bg-primary-light text-white rounded-xl shadow-md hover:shadow-lg"
                  title="Manage project visibility"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  <span>Manage Projects</span>
                </button>
              )}
            </div>
          </div>

          {/* Content Area (scrolls) */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col">
            {/* Authentication Error State */}
            {!isCheckingAuth && !jiraSettingsLoading && isNotAuthenticated ? (
              <div className="px-6 py-8 flex flex-col items-center justify-center flex-1">
                <div className="max-w-md w-full space-y-6">
                  {/* Error Banner */}
                  <div className="p-4 border border-red-400 bg-red-50 dark:bg-red-900/30 dark:border-red-600 rounded-lg">
                    <div className="flex items-center">
                      <HiInformationCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Authentication failed. Please check your username, API
                        token, and base URL in Settings.
                      </p>
                    </div>
                  </div>

                  {/* Info Card */}
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <SiJira className="w-7 h-7 text-[#0052CC]" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                      Jira Not Authenticated
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Your Jira integration credentials are invalid or have
                      expired. Please update your settings to continue importing
                      projects.
                    </p>
                    <CustomButton
                      variant="primary"
                      onClick={() => {
                        handleClose();
                        navigate("/settings#Integrations");
                      }}
                    >
                      Go to Integration Settings
                    </CustomButton>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`px-6 pt-0 flex-1 flex flex-col min-h-0 ${activeTab === "import" ? "pb-0 overflow-hidden" : "pb-24"}`}
              >
                {/* Tab Switcher with Animation (sticky) */}
                <div className="py-4 sticky top-0 z-10 bg-tertiary-light dark:bg-tertiary-dark">
                  <div className="relative flex w-full p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    {/* Sliding Background Indicator */}
                    <div
                      className={`absolute top-1 bottom-1 w-[calc(25%-3px)] bg-white dark:bg-gray-700 rounded-md shadow-sm border border-gray-200 dark:border-gray-600 transition-all duration-300 ease-in-out transform ${
                        activeTab === "import"
                          ? "translate-x-0"
                          : activeTab === "states"
                            ? "translate-x-[calc(100%)]"
                            : activeTab === "priorities_mapping"
                              ? "translate-x-[calc(200%)]"
                              : "translate-x-[calc(300%)]"
                      }`}
                    />

                    <button
                      type="button"
                      onClick={() => setActiveTab("import")}
                      className={`relative z-10 flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                        activeTab === "import"
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transition-transform duration-300 ${activeTab === "import" ? "scale-110" : "scale-100"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                        ></path>
                      </svg>
                      Import Epic
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("states")}
                      className={`relative z-10 flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                        activeTab === "states"
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transition-transform duration-300 ${activeTab === "states" ? "scale-110" : "scale-100"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        ></path>
                      </svg>
                      State Mappings
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("priorities_mapping")}
                      className={`relative z-10 flex-1 whitespace-nowrap inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                        activeTab === "priorities_mapping"
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transition-transform duration-300 ${activeTab === "priorities_mapping" ? "scale-110" : "scale-100"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                        ></path>
                      </svg>
                      Priority Mappings
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("sprints")}
                      className={`relative z-10 flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                        activeTab === "sprints"
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      {/* Theme-aware sprint icon */}
                      <img
                        src="/sprints_light.png"
                        alt="Sprints"
                        className="w-5 h-5 mr-2 dark:hidden"
                      />
                      <img
                        src="/sprints-dark.png"
                        alt="Sprints"
                        className="w-5 h-5 mr-2 hidden dark:inline"
                      />
                      <span>Sprints</span>
                    </button>
                  </div>
                </div>

                {/* Content Area with Transition */}
                <div className="relative h-full flex flex-col">
                  {/* Import Tab Content */}
                  <div
                    className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                      activeTab === "import"
                        ? "opacity-100 translate-x-0 pointer-events-auto"
                        : "opacity-0 translate-x-4 pointer-events-none"
                    }`}
                  >
                    <div className="space-y-6">
                      {isLoading || isCheckingAuth ? (
                        <SkeletonLoader />
                      ) : (
                        <>
                          <div>
                            <label className="block mb-2 text-sm font-medium text-light-text-primary dark:text-white">
                              Choose Your Jira Project
                              <span className="ml-1 text-red-500">*</span>
                            </label>
                            {/* Disabled state handling */}
                            {isImporting ? (
                              <div className="w-full p-3 text-sm bg-light-input-bgDisabled dark:bg-secondary-dark text-light-text-disabled dark:text-gray-400 border border-light-border-default dark:border-gray-600 rounded-lg cursor-not-allowed">
                                {selectedProjectId
                                  ? projects.find(
                                      (p) => p.value === selectedProjectId,
                                    )?.label
                                  : "Select a project..."}
                              </div>
                            ) : (
                              <SingleSelect
                                name="project"
                                options={projects}
                                placeholder="Select a project..."
                                onChange={(option) => {
                                  setSelectedProjectId(option?.value || "");
                                }}
                                defaultValue={
                                  selectedProjectId
                                    ? projects.find(
                                        (p) => p.value === selectedProjectId,
                                      )
                                    : undefined
                                }
                                width="w-full"
                                className="text-sm"
                              />
                            )}
                          </div>

                          <div className="w-full">
                            <div className="flex items-center justify-between mb-2">
                              <label className="flex items-center text-sm font-medium text-light-text-primary dark:text-white whitespace-nowrap">
                                Choose Epic(s) to Import
                                <span className="ml-1 text-red-500">*</span>
                                {isLoadingEpics && selectedProjectId && (
                                  <div className="ml-3 flex items-center gap-2">
                                    <div className="w-3 h-3 border-2 border-primary-light dark:border-primary-dark border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                )}
                              </label>
                              {showEpicLimitWarning && !isDevMode && (
                                <div className="ml-4 flex  items-center text-red-500 dark:text-red-400 text-xs whitespace-nowrap">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3 mr-1 flex-shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <span>
                                    Only 5 epics can be imported at a time
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Disabled state handling */}
                            {!selectedProjectId || isImporting ? (
                              <div className="w-full p-3 text-sm bg-light-input-bgDisabled dark:bg-secondary-dark text-light-text-disabled dark:text-gray-400 border border-light-border-default dark:border-gray-600 rounded-lg cursor-not-allowed">
                                {selectedEpicIds.length > 0
                                  ? `${selectedEpicIds.length} epic(s) selected`
                                  : selectedProjectId
                                    ? "Select epic(s)..."
                                    : "Select a project first"}
                              </div>
                            ) : isLoadingEpics ? (
                              <div className="w-full p-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Loading epics...
                                </span>
                              </div>
                            ) : (
                              <MultiSelect
                                name="epic"
                                options={epics}
                                placeholder={
                                  selectedProjectId
                                    ? "Select epic(s)..."
                                    : "Select a project first"
                                }
                                optionTooltip={(option: any) =>
                                  option?.isImported && option?.isDisabled
                                    ? "This epic is already imported. Use sync functionality for syncing this epic"
                                    : null
                                }
                                onChange={(selectedOptions: any) => {
                                  const newSelectedIds = selectedOptions
                                    ? selectedOptions.map(
                                        (opt: any) => opt.value,
                                      )
                                    : [];
                                  if (!isDevMode && newSelectedIds.length > 5) {
                                    setShowEpicLimitWarning(true);
                                    return;
                                  }

                                  setShowEpicLimitWarning(false);
                                  setSelectedEpicIds(newSelectedIds);
                                }}
                                value={selectedEpicIds
                                  .map((id) =>
                                    epics.find((e) => e.value === id),
                                  )
                                  .filter(Boolean)}
                                width="w-full"
                                className="text-sm"
                              />
                            )}
                          </div>

                          <div>
                            <label className="flex items-center mb-2 text-sm font-medium text-light-text-primary dark:text-white">
                              Choose Board
                              <span className="ml-1 text-red-500">*</span>
                              {isLoadingEpics && selectedProjectId && (
                                <div className="ml-3 flex items-center gap-2">
                                  <div className="w-3 h-3 border-2 border-primary-light dark:border-primary-dark border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                            </label>
                            {/* Disabled state handling */}
                            {!selectedProjectId || isImporting ? (
                              <div className="w-full p-3 text-sm bg-light-input-bgDisabled dark:bg-secondary-dark text-light-text-disabled dark:text-gray-400 border border-light-border-default dark:border-gray-600 rounded-lg cursor-not-allowed">
                                {selectedBoard &&
                                boards.some((b) => b.value === selectedBoard)
                                  ? boards.find(
                                      (b) => b.value === selectedBoard,
                                    )?.label
                                  : selectedProjectId
                                    ? "Select a board..."
                                    : "Select a project first"}
                              </div>
                            ) : isLoadingEpics ? (
                              <div className="w-full p-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Loading boards...
                                </span>
                              </div>
                            ) : (
                              <SingleSelect
                                name="board"
                                options={boards}
                                placeholder={
                                  selectedProjectId
                                    ? "Select a board..."
                                    : "Select a project first"
                                }
                                onChange={(option) => {
                                  setSelectedBoard(option?.value || "");
                                }}
                                defaultValue={
                                  selectedBoard &&
                                  boards.some((b) => b.value === selectedBoard)
                                    ? boards.find(
                                        (b) => b.value === selectedBoard,
                                      )
                                    : undefined
                                }
                                width="w-full"
                                className="text-sm"
                              />
                            )}
                          </div>

                          {/* Configure State Mappings Button */}
                          {shouldShowConfigureStateButton && (
                            <div className="mt-6">
                              <button
                                onClick={handleShowStateMappings}
                                className="w-full py-3 px-4 flex items-center justify-center gap-2 bg-primary-light hover:bg-primary-hover text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Configure State Mappings
                              </button>
                            </div>
                          )}

                          {/* API Message Display */}
                          {displayMessage &&
                            displayMessage.trim() !== "" &&
                            selectedEpicIds.length > 0 &&
                            selectedBoard && (
                              <div className="mt-4 p-4 border border-light-status-warning-border bg-light-status-warning-bg dark:bg-yellow-900/30 dark:border-yellow-600 rounded-lg">
                                <div className="flex items-start">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 text-light-status-warning-text dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <p className="text-sm text-light-status-warning-text dark:text-yellow-300 leading-relaxed">
                                    {displayMessage}
                                  </p>
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Project States Tab Content */}
                  <div
                    className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                      activeTab === "states"
                        ? "opacity-100 translate-x-0 pointer-events-auto"
                        : "opacity-0 -translate-x-4 pointer-events-none"
                    }`}
                  >
                    <div className="space-y-6">
                      <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-light-status-info-bg dark:bg-blue-900/30 p-4 rounded-lg border border-light-status-info-border dark:border-blue-600">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-light-status-info-text dark:text-blue-300 mb-1">
                            Workflow State Configuration
                          </h3>
                          <p className="text-sm text-light-text-secondary dark:text-gray-400">
                            Map your Jira workflow statuses (e.g., To Do, In
                            Progress, Done) to CadencePro completion
                            percentages. This ensures accurate progress tracking
                            for each team.
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            onClick={handleRefreshStatusStages}
                            disabled={isSyncing}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-light hover:text-primary-hover dark:text-primary-dark dark:hover:text-primary-light border border-primary-light hover:border-primary-hover dark:border-primary-dark dark:hover:border-primary-light bg-secondary-light dark:bg-secondary-dark rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {isSyncing ? "Syncing..." : "Sync"}
                          </button>
                        </div>
                      </div>

                      {/* State Mapping Component */}
                      <StateMappingComponent
                        localMappings={statesMappings}
                        setLocalMappings={setStatesMappings}
                        localMappingsOrder={statesMappingsOrder}
                        isJsonView={isStatesJsonView}
                        setIsJsonView={setIsStatesJsonView}
                        jsonInput={statesJsonInput}
                        setJsonInput={setStatesJsonInput}
                        baselineMappings={savedStatesMappings}
                        baselineOrder={savedStatesMappingsOrder}
                        onDirtyChange={setHasUnsavedChanges}
                        isLoading={isLoadingStates}
                      />
                    </div>
                  </div>

                  {/* Priority Mappings Tab Content */}
                  <div
                    className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                      activeTab === "priorities_mapping"
                        ? "opacity-100 translate-x-0 pointer-events-auto"
                        : "opacity-0 translate-x-4 pointer-events-none"
                    }`}
                  >
                    <div className="flex flex-col h-full">
                      <PriorityMappingSelector 
                        ref={priorityRef}
                        integrationType="jira"
                        showInternalUpdate={false}
                        onHasChangesChange={setPriorityHasChanges}
                        onIsSavingChange={setIsSavingPriorities}
                      />
                    </div>
                  </div>

                  {/* Project Sprints Tab Content */}
                  <div
                    className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                      activeTab === "sprints"
                        ? "opacity-100 translate-x-0 pointer-events-auto"
                        : "opacity-0 translate-x-4 pointer-events-none"
                    }`}
                  >
                    <div className="flex flex-col h-full">
                      <SprintsList defaultType="jira" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Button Bar */}
          <div className="sticky bottom-0 left-0 right-0 shrink-0 p-4 bg-secondary-light dark:bg-secondary-dark border-t border-gray-500 rounded-t-md dark:border-gray-700 flex justify-between items-center shadow-lg z-10">
            <CustomButton
              onClick={handleClose}
              variant="cancel"
              disabled={isImporting || isLoadingStates}
            >
              Cancel
            </CustomButton>

            {activeTab === "import" ? (
              <CustomButton
                onClick={handleImport}
                disabled={Boolean(
                  isImporting ||
                  isLoading ||
                  !selectedProjectId ||
                  selectedEpicIds.length === 0 ||
                  !selectedBoard ||
                  (displayMessage &&
                    displayMessage.trim() !== "" &&
                    selectedEpicIds.length > 0 &&
                    selectedBoard &&
                    !showStateMappings),
                )}
                isLoading={isImporting}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import Epic
              </CustomButton>
            ) : activeTab === "states" ? (
              <div className="flex items-center gap-3">
                <UnsavedChangesIndicator
                  hasUnsavedChanges={hasUnsavedChanges}
                />
                <CustomButton
                  onClick={handleSaveStateMappings}
                  variant="primary"
                  className="bg-primary-light dark:bg-primary-dark hover:bg-primary-hover"
                  isLoading={isLoadingStates}
                  disabled={isLoadingStates}
                >
                  Save Mappings
                </CustomButton>
              </div>
            ) : activeTab === "priorities_mapping" ? (
              <div className="flex gap-3 items-center">
                <UnsavedChangesIndicator
                  hasUnsavedChanges={priorityHasChanges}
                />
                <CustomButton
                  onClick={() => priorityRef.current?.update()}
                  variant="primary"
                  className="bg-primary-light dark:bg-primary-dark hover:bg-primary-hover"
                  isLoading={isSavingPriorities}
                  disabled={isSavingPriorities || !priorityHasChanges}
                >
                  Save Mappings
                </CustomButton>
              </div>
            ) : null}
          </div>
        </div>
      </Drawer>

      {/* State Mapping Modal */}
      <StateMappingModal
        isOpen={showStateMappingModal}
        onClose={() => setShowStateMappingModal(false)}
        localMappings={localMappings}
        setLocalMappings={setLocalMappings}
        localMappingsOrder={localMappingsOrder}
        isJsonView={isJsonView}
        setIsJsonView={setIsJsonView}
        jsonInput={jsonInput}
        setJsonInput={setJsonInput}
      />

      {/* Manage Projects Modal */}
      <ManageProjectsModal
        isOpen={showManageProjectsModal}
        onClose={() => setShowManageProjectsModal(false)}
        processedProjects={processedProjects}
        onSave={async (changedProjects) => {
          await manageJiraProjects(changedProjects);
          // Refresh all related data after successful update
          await Promise.all([
            loadSettings(),
            fetchJiraProjects()
              .then(setProjects)
              .catch(() => {}),
            fetchSprints("jira", "current").catch(() => {}),
          ]);
        }}
        onResync={async (projectName) => {
          // Find the project ID from the projects array
          const project = projects.find((p) => p.label === projectName);
          const projectId = project?.value || projectName;
          await syncIntegrationProject(projectName, "jira", projectId);
        }}
        integrationType="jira"
        isLoading={isLoadingStates}
      />
    </>
  );
};

export default ImportFromJiraModal;
